import { BriefDeliverableStatusEnum } from "@entities/brief/lib/constants";
import { useGetDraftById } from "@entities/brief/lib/hooks/darfting/useGetDraftById";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import UploadModal from "@entities/videoPlayerV2/ui/UploadModal";
import { ICampaignDeliverableTaskForm } from "@shared/lib/types/briefCampains";
import { Button, Modal } from "@shared/ui";
import CampaignDraftDrawer from "@widgets/CampaignDraftDrawer";
import dayjs from "dayjs";
import { AlertCircle, ArrowRight, CheckCircle2, Clock, Plus, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import styles from "./styles.module.scss";

interface AddCampaignDraftProps {
  campaignInfluencerId: string;
  deliverableConfigRefId: string;
  taskId: string;
  taskForm?: ICampaignDeliverableTaskForm;
  taskStatus: BriefDeliverableStatusEnum;
  refetchInfluencer: () => void;
  draftId: string;
  briefId: string;
}

type DraftStatus =
  | "pending"
  | "in_progress"
  | "in_review"
  | "needs_approval"
  | "changes_requested"
  | "completed";

export default function AddCampaignDraft({
  campaignInfluencerId,
  deliverableConfigRefId,
  taskId,
  taskForm,
  taskStatus,
  refetchInfluencer,
  draftId,
  briefId,
}: AddCampaignDraftProps) {
  const [isAddDraftVisible, setIsAddDraftVisible] = useState(false);
  const [isDraftDrawerOpen, setIsDraftDrawerOpen] = useState(false);

  const { drafting } = useGetDraftById(draftId);

  const { userDetails } = useGetMe();
  // const isAddDraftDisabled =
  //   userDetails?.userEntity?.permissions.includes("super-brand-org") ||
  //   userDetails?.userEntity?.permissions.includes("super")
  //     ? false
  //     : !userDetails?.userEntity?.permissions.includes("modify-briefs-org");

      // getting unresolved comment of the latest version of draft
  const latestUnresolvedComment = useMemo(
    () =>
      drafting?.drafting?.draftingContents?.[drafting?.drafting?.draftingContents?.length - 1]?.comments?.filter((comment) => !comment.isResolved) || [],
    [drafting?.drafting?.draftingContents]
  );

  const hasDraftId = !!draftId;

  const draftStatus: DraftStatus = useMemo(() => {
    // If no script ID at all, show pending/empty state
    if (!hasDraftId) {
      return "pending";
    }

    // If script ID exists but no scripting data yet, or status is pending, show in progress
    if (hasDraftId && taskStatus === BriefDeliverableStatusEnum.Pending) {
      return "in_progress";
    }

    switch (taskStatus) {
      case BriefDeliverableStatusEnum.PendingApproval:
        return "needs_approval";
      case BriefDeliverableStatusEnum.EditsRequested:
        return "changes_requested";
      case BriefDeliverableStatusEnum.Approved:
      case BriefDeliverableStatusEnum.Completed:
        return "completed";
      case BriefDeliverableStatusEnum.Uploaded:
        return "in_review";
      default:
        return "in_progress";
    }
  }, [hasDraftId, taskStatus]);

  const handleCreateDraft = () => {
    setIsAddDraftVisible(true);
  };

  const handleViewDraft = () => {
    if (draftId) {
      setIsDraftDrawerOpen(true);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = dayjs(dateStr);
    return date.format("MMM DD, h:mm A");
  };

  const getUploadedDate = () => {
    if (drafting?.drafting?.createdAt) {
      return formatDate(drafting.drafting.createdAt);
    }
    return "";
  };

  const renderCommentsPreview = () => {
    if (latestUnresolvedComment.length === 0) return null;

    return (
      <div className={styles.commentsPreview}>
        <div className={styles.commentsTitle}>Latest Feedback</div>
        <div className={styles.commentsList}>
          {latestUnresolvedComment.map((comment) => (
            <div key={comment._id} className={styles.commentItem}>
              <div className={styles.commentBar}></div>
              <div className={styles.commentContent}>
                <span className={styles.commentUser}>
                  {comment.commentedBy?.firstName}{" "}
                  {comment.commentedBy?.lastName}:{" "}
                </span>
                <span className={styles.commentText}>{comment.text}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (draftStatus === "pending") {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <Clock size={18} />
          </div>
          <p className={styles.emptyTextTitle}>Draft pending</p>
          <p className={styles.emptyTextDescription}>
            Draft is still pending from the creator. You will be able to review
            it once it is submitted.
          </p>
            {/* <DisabledPopover isDisabled={isAddDraftDisabled}> */}
              <Button
                // disabled={isAddDraftDisabled}
                type="primary"
                icon={<Plus size={14} />}
                onClick={handleCreateDraft}
                className={styles.createButton}
              >
                Create Draft
              </Button>
            {/* </DisabledPopover> */}
        </div>
      );
    }

    if (draftStatus === "in_progress") {
      return (
        <div className={styles.statusContainer}>
          {renderCommentsPreview()}
          <div className={styles.statusFooter}>
            {/* <span className={styles.statusText}>In Progress</span> */}
            <Button
              type="link"
              icon={<ArrowRight size={12} />}
              onClick={handleViewDraft}
              className={styles.viewButton}
            >
              View Draft
            </Button>
          </div>
        </div>
      );
    }

    if (draftStatus === "in_review") {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusBanner} data-status="in_review">
            <Clock size={16} className={styles.statusIcon} />
            <div>
              <div className={styles.statusTitle}>In Internal Review</div>
              <p className={styles.statusDescription}>
                This Draft is currently under internal review. You can override
                and approve or request changes directly.
              </p>
            </div>
          </div>
          {renderCommentsPreview()}
          <div className={styles.statusFooter}>
            <span className={styles.dateText}>
              {getUploadedDate() ? `Uploaded on ${getUploadedDate()}` : ""}
            </span>
            <Button
              type="link"
              icon={<ArrowRight size={12} />}
              onClick={handleViewDraft}
              className={styles.viewButton}
            >
              View Draft
            </Button>
          </div>
        </div>
      );
    }

    if (draftStatus === "needs_approval") {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusBanner} data-status="needs_approval">
            <AlertCircle size={16} className={styles.statusIcon} />
            <div>
              <div className={styles.statusTitle}>Awaiting Brand Review</div>
              <p className={styles.statusDescription}>
                Please review the Draft and provide feedback or approve it to
                proceed.
              </p>
            </div>
          </div>
          {renderCommentsPreview()}
          <div className={styles.statusFooter}>
            <span className={styles.dateText}>
              {getUploadedDate() ? `Uploaded on ${getUploadedDate()}` : ""}
            </span>
            <Button
              type="primary"
              onClick={handleViewDraft}
              className={styles.reviewButton}
            >
              Review Draft
            </Button>
          </div>
        </div>
      );
    }

    if (draftStatus === "changes_requested") {
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusBanner} data-status="changes_requested">
            <XCircle size={16} className={styles.statusIcon} />
            <div>
              <div className={styles.statusTitle}>Changes Requested</div>
              <p className={styles.statusDescription}>
                Feedback sent to creator. Waiting for revised version.
              </p>
            </div>
          </div>

          {renderCommentsPreview()}

          <div className={styles.statusFooter}>
            <Button
              type="link"
              onClick={handleViewDraft}
              className={styles.viewDetailsButton}
            >
              View Details
            </Button>
          </div>
        </div>
      );
    }

    if (draftStatus === "completed") {
      const approvedItem = [
        ...(drafting?.campaignInfluencerTaskState?.timeline || []),
      ]
        .reverse()
        ?.find((item) => item.status === BriefDeliverableStatusEnum.Approved);
      // const approvedItem =
      return (
        <div className={styles.statusContainer}>
          <div className={styles.statusBanner} data-status="completed">
            <CheckCircle2 size={16} className={styles.statusIcon} />
            <span className={styles.statusTitle}>Draft Approved</span>
          </div>
          <div className={styles.completedInfo}>
            <div>
              Uploaded:{" "}
              <span className={styles.infoValue}>
                {getUploadedDate()
                  ? dayjs(drafting?.drafting?.createdAt).format("MMM DD")
                  : ""}
              </span>
            </div>
            {approvedItem && (
              <div>
                Approved:{" "}
                <span className={styles.infoValue}>
                  {approvedItem?.createdAt
                    ? `${dayjs(approvedItem.createdAt).format("MMM DD")} by ${
                        approvedItem.createdBy?.firstName || ""
                      }`
                    : ""}
                </span>
              </div>
            )}
          </div>
          <Button
            block
            onClick={handleViewDraft}
            className={styles.viewApprovedButton}
          >
            View Draft
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles.container}>
      {renderContent()}

      <CampaignDraftDrawer
        open={isDraftDrawerOpen}
        onClose={() => setIsDraftDrawerOpen(false)}
        draftId={draftId}
        refetchInfluencer={refetchInfluencer}
      />

      <Modal
        open={isAddDraftVisible}
        onCancel={() => setIsAddDraftVisible(false)}
        footer={null}
      >
        {/* <AddBriefCampaignDraft
          taskId={taskId}
          refetchInfluencer={refetchInfluencer}
          campaignInfluencerId={campaignInfluencerId}
          deliverableConfigRefId={deliverableConfigRefId}
          onClose={() => setIsAddDraftVisible(false)}
        /> */}

        <UploadModal
          refetch={refetchInfluencer}
          draftingId={draftId}
          taskId={taskId}
          campaignInfluencerId={campaignInfluencerId}
          deliverableConfigRefId={deliverableConfigRefId}
          onClose={() => setIsAddDraftVisible(false)}
          briefId={briefId}
          // setLatestVersionId={() => {}}
        />
      </Modal>
    </div>
  );
}
